import "antd/es/spin/style";
import _Spin from "antd/es/spin";
import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import "antd/es/form/style";
import _Form from "antd/es/form";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import "antd/es/config-provider/style";
import _ConfigProvider from "antd/es/config-provider";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children", "contentRender", "submitter", "fieldProps", "formItemProps", "groupProps", "dateFormatter", "formRef", "onInit", "form", "formComponentType", "extraUrlParams", "syncToUrl", "syncToInitialValues", "onReset", "omitNil", "isKeyPressSubmit", "autoFocusFirstInput", "grid", "rowProps", "colProps"],
    _excluded2 = ["request", "params", "initialValues", "formKey"];
import React, { useRef, useEffect, useImperativeHandle, useState, useMemo, useCallback, useContext } from 'react';
import { ConfigProviderWrap } from '@ant-design/pro-provider';
import set from "rc-util/es/utils/set";
import { conversionMomentValue, transformKeySubmitValue, useMountMergeState, ProFormContext, runFunction, useFetchData, isDeepEqualReact, usePrevious } from '@ant-design/pro-utils';
import { useUrlSearchParams } from '@umijs/use-params';
import namePathSet from "rc-util/es/utils/set";
import FieldContext from '../FieldContext';
import { Submitter } from '../components';
import { noteOnce } from "rc-util/es/warning";
import get from "rc-util/es/utils/get";
import { useGridHelpers } from '../helpers';

var genParams = function genParams(syncUrl, params, type) {
  if (syncUrl === true) {
    return params;
  }

  return runFunction(syncUrl, params, type);
};
/**
 * It takes a name path and converts it to an array.
 * @param {NamePath} name - The name of the form.
 * @returns string[]
 *
 * a-> [a]
 * [a] -> [a]
 */


var covertFormName = function covertFormName(name) {
  if (!name) return name;
  if (Array.isArray(name)) return name;
  return [name];
};

function BaseFormComponents(props) {
  var children = props.children,
      contentRender = props.contentRender,
      submitter = props.submitter,
      fieldProps = props.fieldProps,
      formItemProps = props.formItemProps,
      groupProps = props.groupProps,
      _props$dateFormatter = props.dateFormatter,
      dateFormatter = _props$dateFormatter === void 0 ? 'string' : _props$dateFormatter,
      propsFormRef = props.formRef,
      onInit = props.onInit,
      form = props.form,
      formComponentType = props.formComponentType,
      _props$extraUrlParams = props.extraUrlParams,
      extraUrlParams = _props$extraUrlParams === void 0 ? {} : _props$extraUrlParams,
      syncToUrl = props.syncToUrl,
      _props$syncToInitialV = props.syncToInitialValues,
      syncToInitialValues = _props$syncToInitialV === void 0 ? true : _props$syncToInitialV,
      _onReset = props.onReset,
      _props$omitNil = props.omitNil,
      omitNil = _props$omitNil === void 0 ? true : _props$omitNil,
      isKeyPressSubmit = props.isKeyPressSubmit,
      _props$autoFocusFirst = props.autoFocusFirstInput,
      autoFocusFirstInput = _props$autoFocusFirst === void 0 ? true : _props$autoFocusFirst,
      grid = props.grid,
      rowProps = props.rowProps,
      colProps = props.colProps,
      rest = _objectWithoutProperties(props, _excluded);

  var sizeContextValue = useContext(_ConfigProvider.SizeContext);

  var _Form$useForm = _Form.useForm(form),
      _Form$useForm2 = _slicedToArray(_Form$useForm, 1),
      inlineForm = _Form$useForm2[0];
  /** 同步 url 上的参数 */


  var _useUrlSearchParams = useUrlSearchParams({}, {
    disabled: !syncToUrl
  }),
      _useUrlSearchParams2 = _slicedToArray(_useUrlSearchParams, 2),
      urlSearch = _useUrlSearchParams2[0],
      setUrlSearch = _useUrlSearchParams2[1];

  var formRef = useRef(inlineForm || {});

  var _useGridHelpers = useGridHelpers({
    grid: grid,
    rowProps: rowProps,
    colProps: colProps
  }),
      RowWrapper = _useGridHelpers.RowWrapper;

  var fieldsValueType = useRef({});
  /** 保存 transformKeyRef，用于对表单key transform */

  var transformKeyRef = useRef({});
  /** 使用 callback 的类型 */

  var transformKey = useCallback(function (values, omit, parentKey) {
    return transformKeySubmitValue(conversionMomentValue(values, dateFormatter, fieldsValueType.current, omit, parentKey), transformKeyRef.current, omit);
  }, [dateFormatter]);
  var formatValues = useMemo(function () {
    return {
      /**
       * 获取被 ProForm 格式化后的所有数据
       * @param allData boolean
       * @returns T
       *
       * @example  getFieldsFormatValue(true) ->返回所有数据，即使没有被 form 托管的
       */
      getFieldsFormatValue: function getFieldsFormatValue(allData) {
        var _formRef$current;

        return transformKey((_formRef$current = formRef.current) === null || _formRef$current === void 0 ? void 0 : _formRef$current.getFieldsValue(allData), omitNil);
      },

      /**
       * 获取被 ProForm 格式化后的单个数据
       * @param nameList (string|number)[]
       * @returns T
       *
       * @example {a:{b:value}} -> getFieldFormatValue(['a', 'b']) -> value
       */

      /** 获取格式化之后的单个数据 */
      getFieldFormatValue: function getFieldFormatValue() {
        var _formRef$current2;

        var paramsNameList = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
        var nameList = covertFormName(paramsNameList);
        if (!nameList) throw new Error('nameList is require');
        var value = (_formRef$current2 = formRef.current) === null || _formRef$current2 === void 0 ? void 0 : _formRef$current2.getFieldValue(nameList);
        var obj = nameList ? set({}, nameList, value) : value;
        return get(transformKey(obj, omitNil, nameList), nameList);
      },

      /**
       * 获取被 ProForm 格式化后的单个数据, 包含他的 name
       * @param nameList (string|number)[]
       * @returns T
       *
       * @example  {a:{b:value}} -> getFieldFormatValueObject(['a', 'b']) -> {a:{b:value}}
       */

      /** 获取格式化之后的单个数据 */
      getFieldFormatValueObject: function getFieldFormatValueObject(paramsNameList) {
        var _formRef$current3;

        var nameList = covertFormName(paramsNameList);
        var value = (_formRef$current3 = formRef.current) === null || _formRef$current3 === void 0 ? void 0 : _formRef$current3.getFieldValue(nameList);
        var obj = nameList ? set({}, nameList, value) : value;
        return transformKey(obj, omitNil, nameList);
      },

      /**
      /**
       *验字段后返回格式化之后的所有数据
       * @param nameList (string|number)[]
       * @returns T
       *
       * @example validateFieldsReturnFormatValue -> {a:{b:value}}
       */
      validateFieldsReturnFormatValue: function () {
        var _validateFieldsReturnFormatValue = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(nameList) {
          var _formRef$current4;

          var values;
          return _regeneratorRuntime.wrap(function _callee$(_context) {
            while (1) {
              switch (_context.prev = _context.next) {
                case 0:
                  if (!(!Array.isArray(nameList) && nameList)) {
                    _context.next = 2;
                    break;
                  }

                  throw new Error('nameList must be array');

                case 2:
                  _context.next = 4;
                  return (_formRef$current4 = formRef.current) === null || _formRef$current4 === void 0 ? void 0 : _formRef$current4.validateFields(nameList);

                case 4:
                  values = _context.sent;
                  return _context.abrupt("return", transformKey(values, omitNil));

                case 6:
                case "end":
                  return _context.stop();
              }
            }
          }, _callee);
        }));

        function validateFieldsReturnFormatValue(_x) {
          return _validateFieldsReturnFormatValue.apply(this, arguments);
        }

        return validateFieldsReturnFormatValue;
      }(),
      formRef: formRef
    };
  }, [omitNil, transformKey]);
  /** 利用反射把值传的到处都是，并且总是新的 */

  var responseForm = useMemo(function () {
    var response = _objectSpread({}, formRef.current);

    Object.keys(formRef.current || {}).forEach(function (key) {
      Object.defineProperty(response, key, {
        get: function get() {
          return formRef.current[key];
        }
      });
    });
    Object.keys(formatValues).forEach(function (key) {
      Object.defineProperty(response, key, {
        get: function get() {
          return formatValues[key];
        }
      });
    });
    return response; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  var _useMountMergeState = useMountMergeState(false),
      _useMountMergeState2 = _slicedToArray(_useMountMergeState, 2),
      loading = _useMountMergeState2[0],
      setLoading = _useMountMergeState2[1];

  var items = useMemo(function () {
    return React.Children.toArray(children).map(function (item, index) {
      if (index === 0 && /*#__PURE__*/React.isValidElement(item) && autoFocusFirstInput) {
        return /*#__PURE__*/React.cloneElement(item, _objectSpread(_objectSpread({}, item.props), {}, {
          autoFocus: autoFocusFirstInput
        }));
      }

      return item;
    });
  }, [autoFocusFirstInput, children]);
  /** 计算 props 的对象 */

  var submitterProps = useMemo(function () {
    return typeof submitter === 'boolean' || !submitter ? {} : submitter;
  }, [submitter]); // 初始化给一个默认的 form

  useImperativeHandle(propsFormRef, function () {
    return responseForm;
  });
  /** 渲染提交按钮与重置按钮 */

  var submitterNode = useMemo(function () {
    if (submitter === false) return undefined;
    return /*#__PURE__*/React.createElement(Submitter, _extends({
      key: "submitter"
    }, submitterProps, {
      onReset: function onReset() {
        var _formRef$current5, _submitterProps$onRes;

        var finalValues = transformKey((_formRef$current5 = formRef.current) === null || _formRef$current5 === void 0 ? void 0 : _formRef$current5.getFieldsValue(), omitNil);
        submitterProps === null || submitterProps === void 0 ? void 0 : (_submitterProps$onRes = submitterProps.onReset) === null || _submitterProps$onRes === void 0 ? void 0 : _submitterProps$onRes.call(submitterProps, finalValues);
        _onReset === null || _onReset === void 0 ? void 0 : _onReset(finalValues); // 如果 syncToUrl，清空一下数据

        if (syncToUrl) {
          var _formRef$current6;

          // 把没有的值设置为未定义可以删掉 url 的参数
          var params = Object.keys(transformKey((_formRef$current6 = formRef.current) === null || _formRef$current6 === void 0 ? void 0 : _formRef$current6.getFieldsValue(), false)).reduce(function (pre, next) {
            return _objectSpread(_objectSpread({}, pre), {}, _defineProperty({}, next, finalValues[next] || undefined));
          }, extraUrlParams);
          /** 在同步到 url 上时对参数进行转化 */

          setUrlSearch(genParams(syncToUrl, params, 'set'));
        }
      },
      form: responseForm,
      submitButtonProps: _objectSpread({
        loading: loading
      }, submitterProps.submitButtonProps)
    }));
  }, [submitter, submitterProps, responseForm, loading, transformKey, omitNil, _onReset, syncToUrl, extraUrlParams, setUrlSearch]);
  var content = useMemo(function () {
    var wrapItems = grid ? /*#__PURE__*/React.createElement(RowWrapper, null, items) : items;

    if (contentRender) {
      return contentRender(wrapItems, submitterNode, formRef.current);
    }

    return wrapItems;
  }, [grid, RowWrapper, items, contentRender, submitterNode]);
  var getPopupContainer = useMemo(function () {
    if (typeof window === 'undefined') return undefined; // 如果在 drawerForm 和  modalForm 里就渲染dom到父节点里
    // modalForm 可能高度太小不适合

    if (formComponentType && ['DrawerForm'].includes(formComponentType)) {
      return function (e) {
        return e.parentNode || document.body;
      };
    }

    return undefined;
  }, [formComponentType]);
  useEffect(function () {
    var _formRef$current7;

    var finalValues = transformKey((_formRef$current7 = formRef.current) === null || _formRef$current7 === void 0 ? void 0 : _formRef$current7.getFieldsValue(true), omitNil);
    onInit === null || onInit === void 0 ? void 0 : onInit(finalValues, responseForm); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 如果为 false，不需要触发设置进去

  var _useState = useState(function () {
    if (!syncToUrl) {
      return {};
    }

    return genParams(syncToUrl, urlSearch, 'get');
  }),
      _useState2 = _slicedToArray(_useState, 2),
      urlParamsMergeInitialValues = _useState2[0],
      setUrlParamsMergeInitialValues = _useState2[1];

  useEffect(function () {
    if (syncToInitialValues) return;
    setUrlParamsMergeInitialValues({});
  }, [syncToInitialValues]);
  var preInitialValues = usePrevious(props.initialValues); // 提示一个 initialValues ，问的人实在是太多了

  useEffect(function () {
    if (syncToUrl || !props.initialValues || !preInitialValues || rest.request) return;
    var isEqual = isDeepEqualReact(props.initialValues, preInitialValues);
    noteOnce(isEqual, "initialValues \u53EA\u5728 form \u521D\u59CB\u5316\u65F6\u751F\u6548\uFF0C\u5982\u679C\u4F60\u9700\u8981\u5F02\u6B65\u52A0\u8F7D\u63A8\u8350\u4F7F\u7528 request\uFF0C\u6216\u8005 initialValues ? <Form/> : null ");
    noteOnce(isEqual, "The initialValues only take effect when the form is initialized, if you need to load asynchronously recommended request, or the initialValues ? <Form/> : null "); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initialValues]);
  useEffect(function () {
    if (!syncToUrl) return;
    setUrlSearch(_objectSpread(_objectSpread({}, urlSearch), extraUrlParams)); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraUrlParams, syncToUrl]);
  return (
    /*#__PURE__*/
    // 增加国际化的能力，与 table 组件可以统一
    React.createElement(FieldContext.Provider, {
      value: {
        grid: grid,
        formRef: formRef,
        fieldProps: fieldProps,
        formItemProps: formItemProps,
        groupProps: groupProps,
        formComponentType: formComponentType,
        getPopupContainer: getPopupContainer,
        setFieldValueType: function setFieldValueType(name, _ref) {
          var _ref$valueType = _ref.valueType,
              valueType = _ref$valueType === void 0 ? 'text' : _ref$valueType,
              dateFormat = _ref.dateFormat,
              transform = _ref.transform;
          if (!Array.isArray(name)) return;
          transformKeyRef.current = namePathSet(transformKeyRef.current, name, transform);
          fieldsValueType.current = namePathSet(fieldsValueType.current, name, {
            valueType: valueType,
            dateFormat: dateFormat
          });
        }
      }
    }, /*#__PURE__*/React.createElement(ProFormContext.Provider, {
      value: formatValues
    }, /*#__PURE__*/React.createElement(_ConfigProvider.SizeContext.Provider, {
      value: rest.size || sizeContextValue
    }, /*#__PURE__*/React.createElement(_Form, _extends({
      onKeyPress: function onKeyPress(event) {
        if (!isKeyPressSubmit) return;

        if (event.key === 'Enter') {
          var _formRef$current8;

          (_formRef$current8 = formRef.current) === null || _formRef$current8 === void 0 ? void 0 : _formRef$current8.submit();
        }
      },
      form: inlineForm
    }, rest, {
      // 组合 urlSearch 和 initialValues
      initialValues: _objectSpread(_objectSpread({}, urlParamsMergeInitialValues), rest.initialValues),
      onValuesChange: function onValuesChange(changedValues, values) {
        var _rest$onValuesChange;

        rest === null || rest === void 0 ? void 0 : (_rest$onValuesChange = rest.onValuesChange) === null || _rest$onValuesChange === void 0 ? void 0 : _rest$onValuesChange.call(rest, transformKey(changedValues, omitNil), transformKey(values, omitNil));
      },
      onFinish: /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
        var _formRef$current9, finalValues, _formRef$current10, params;

        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                if (rest.onFinish) {
                  _context2.next = 2;
                  break;
                }

                return _context2.abrupt("return");

              case 2:
                if (!loading) {
                  _context2.next = 4;
                  break;
                }

                return _context2.abrupt("return");

              case 4:
                setLoading(true);
                _context2.prev = 5;
                finalValues = transformKey((_formRef$current9 = formRef.current) === null || _formRef$current9 === void 0 ? void 0 : _formRef$current9.getFieldsValue(), omitNil);
                _context2.next = 9;
                return rest.onFinish(finalValues);

              case 9:
                if (syncToUrl) {
                  // 把没有的值设置为未定义可以删掉 url 的参数
                  params = Object.keys(transformKey((_formRef$current10 = formRef.current) === null || _formRef$current10 === void 0 ? void 0 : _formRef$current10.getFieldsValue(), false)).reduce(function (pre, next) {
                    var _finalValues$next;

                    return _objectSpread(_objectSpread({}, pre), {}, _defineProperty({}, next, (_finalValues$next = finalValues[next]) !== null && _finalValues$next !== void 0 ? _finalValues$next : undefined));
                  }, extraUrlParams); // fix #3547: 当原先在url中存在的字段被删除时，应该将 params 中的该字段设置为 undefined,以便触发url同步删除

                  Object.keys(urlSearch).forEach(function (key) {
                    if (params[key] !== false && params[key] !== 0 && !params[key]) {
                      params[key] = undefined;
                    }
                  });
                  /** 在同步到 url 上时对参数进行转化 */

                  setUrlSearch(genParams(syncToUrl, params, 'set'));
                }

                setLoading(false);
                _context2.next = 16;
                break;

              case 13:
                _context2.prev = 13;
                _context2.t0 = _context2["catch"](5);
                // console.log(error);
                setLoading(false);

              case 16:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2, null, [[5, 13]]);
      }))
    }), rest.component !== false && /*#__PURE__*/React.createElement("input", {
      type: "text",
      style: {
        display: 'none'
      }
    }), /*#__PURE__*/React.createElement(_Form.Item, {
      noStyle: true,
      shouldUpdate: true
    }, function (formInstance) {
      if (propsFormRef) propsFormRef.current = _objectSpread(_objectSpread({}, formInstance), formatValues);
      formRef.current = formInstance;
      return null;
    }), content))))
  );
}
/** 自动的formKey 防止重复 */


var requestFormCacheId = 0;

function BaseForm(props) {
  var request = props.request,
      params = props.params,
      initialValues = props.initialValues,
      _props$formKey = props.formKey,
      formKey = _props$formKey === void 0 ? requestFormCacheId : _props$formKey,
      rest = _objectWithoutProperties(props, _excluded2);

  useEffect(function () {
    requestFormCacheId += 0;
  }, []);

  var _useFetchData = useFetchData({
    request: request,
    params: params,
    proFieldKey: formKey
  }),
      _useFetchData2 = _slicedToArray(_useFetchData, 1),
      initialData = _useFetchData2[0];

  if (!initialData && props.request) {
    return /*#__PURE__*/React.createElement("div", {
      style: {
        paddingTop: 50,
        paddingBottom: 50,
        textAlign: 'center'
      }
    }, /*#__PURE__*/React.createElement(_Spin, null));
  }

  return /*#__PURE__*/React.createElement(ConfigProviderWrap, null, /*#__PURE__*/React.createElement(BaseFormComponents, _extends({
    autoComplete: "off"
  }, rest, {
    initialValues: _objectSpread(_objectSpread({}, initialValues), initialData)
  })));
}

export { BaseForm };